import { z } from 'zod';
import { Group } from '../paths/herawApiPathGroups';
import { ApiMethod } from '../types/api.types';
import {
    customFieldAssociationSchema,
    customFieldOptionSchema,
    customFieldSchema,
    customFieldWithAssociationsSchema
} from '../types/custom-field.types';
import {
    createCustomFieldBodySchema,
    createCustomFieldOptionBodySchema,
    customFieldAssociationParamsSchema,
    customFieldOptionParamsSchema,
    customFieldParamsSchema,
    updateCustomFieldBodySchema,
    updateCustomFieldOptionBodySchema
} from '../types/payloads/custom-field.payload';
import { workspaceParamsSchema } from '../types/payloads/workspace.payload';

export const API_CUSTOM_FIELDS = {
    LIST: {
        method: ApiMethod.GET,
        path: '/workspaces/:workspaceName/custom-fields',
        handlerMethod: 'listCustomFields',
        zod: {
            params: workspaceParamsSchema
        },
        responses: {
            200: {
                description: 'List of custom fields',
                schema: z.array(customFieldWithAssociationsSchema)
            }
        }
    },
    LIST_OA: {
        method: ApiMethod.GET,
        path: '/public/v1/workspaces/:workspaceName/custom-fields',
        handlerMethod: 'listCustomFields',
        publicApi: true,
        description: 'List all custom fields for a workspace',
        group: Group.CustomFields,
        zod: {
            params: workspaceParamsSchema
        },
        responses: {
            200: {
                description: 'List of custom fields',
                schema: z.array(customFieldWithAssociationsSchema)
            }
        }
    },
    POST: {
        method: ApiMethod.POST,
        path: '/workspaces/:workspaceName/custom-fields',
        handlerMethod: 'createCustomField',
        zod: {
            params: workspaceParamsSchema,
            body: createCustomFieldBodySchema
        },
        responses: {
            200: {
                description: 'Custom field successfully created',
                schema: customFieldSchema
            }
        }
    },
    POST_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/custom-fields',
        handlerMethod: 'createCustomField',
        publicApi: true,
        description: 'Create a custom field',
        group: Group.CustomFields,
        zod: {
            params: workspaceParamsSchema,
            body: createCustomFieldBodySchema
        },
        responses: {
            200: {
                description: 'Custom field successfully created',
                schema: customFieldSchema
            }
        }
    },
    PATCH: {
        method: ApiMethod.PATCH,
        path: '/workspaces/:workspaceName/custom-fields/:customFieldUuid',
        handlerMethod: 'updateCustomField',
        zod: {
            params: customFieldParamsSchema,
            body: updateCustomFieldBodySchema
        },
        responses: {
            204: {
                description: 'Custom field successfully updated'
            }
        }
    },
    PATCH_OA: {
        method: ApiMethod.PATCH,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid',
        handlerMethod: 'updateCustomField',
        publicApi: true,
        description: 'Update a custom field',
        group: Group.CustomFields,
        zod: {
            params: customFieldParamsSchema,
            body: updateCustomFieldBodySchema
        },
        responses: {
            204: {
                description: 'Custom field successfully updated'
            }
        }
    },
    DELETE: {
        method: ApiMethod.DELETE,
        path: '/workspaces/:workspaceName/custom-fields/:customFieldUuid',
        handlerMethod: 'deleteCustomField',
        zod: {
            params: customFieldParamsSchema
        },
        responses: {
            204: {
                description: 'Custom field successfully deleted'
            }
        }
    },
    DELETE_OA: {
        method: ApiMethod.DELETE,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid',
        handlerMethod: 'deleteCustomField',
        publicApi: true,
        description: 'Delete a custom field',
        group: Group.CustomFields,
        zod: {
            params: customFieldParamsSchema
        },
        responses: {
            204: {
                description: 'Custom field successfully deleted'
            }
        }
    }
} as const;

export const API_CUSTOM_FIELD_OPTIONS = {
    POST: {
        method: ApiMethod.POST,
        path: '/workspaces/:workspaceName/custom-fields/:customFieldUuid/options',
        handlerMethod: 'createCustomFieldOption',
        zod: {
            params: customFieldParamsSchema,
            body: createCustomFieldOptionBodySchema
        },
        responses: {
            200: {
                description: 'Custom field option successfully created',
                schema: customFieldOptionSchema
            }
        }
    },
    POST_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/options',
        handlerMethod: 'createCustomFieldOption',
        publicApi: true,
        description: 'Create a custom field option',
        group: Group.CustomFields,
        zod: {
            params: customFieldParamsSchema,
            body: createCustomFieldOptionBodySchema
        },
        responses: {
            200: {
                description: 'Custom field option successfully created',
                schema: customFieldOptionSchema
            }
        }
    },
    PATCH: {
        method: ApiMethod.PATCH,
        path: '/workspaces/:workspaceName/custom-fields/:customFieldUuid/options/:customFieldOptionUuid',
        handlerMethod: 'updateCustomFieldOption',
        zod: {
            params: customFieldOptionParamsSchema,
            body: updateCustomFieldOptionBodySchema
        },
        responses: {
            204: {
                description: 'Custom field option successfully updated'
            }
        }
    },
    PATCH_OA: {
        method: ApiMethod.PATCH,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/options/:customFieldOptionUuid',
        handlerMethod: 'updateCustomFieldOption',
        publicApi: true,
        description: 'Update a custom field option',
        group: Group.CustomFields,
        zod: {
            params: customFieldOptionParamsSchema,
            body: updateCustomFieldOptionBodySchema
        },
        responses: {
            204: {
                description: 'Custom field option successfully updated'
            }
        }
    },
    DELETE: {
        method: ApiMethod.DELETE,
        path: '/workspaces/:workspaceName/custom-fields/:customFieldUuid/options/:customFieldOptionUuid',
        handlerMethod: 'removeCustomFieldOption',
        zod: {
            params: customFieldOptionParamsSchema
        },
        responses: {
            204: {
                description: 'Custom field option successfully deleted'
            }
        }
    },
    DELETE_OA: {
        method: ApiMethod.DELETE,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/options/:customFieldOptionUuid',
        handlerMethod: 'removeCustomFieldOption',
        publicApi: true,
        description: 'Delete a custom field option',
        group: Group.CustomFields,
        zod: {
            params: customFieldOptionParamsSchema
        },
        responses: {
            204: {
                description: 'Custom field option successfully deleted'
            }
        }
    }
} as const;

export const API_CUSTOM_FIELD_ASSOCIATIONS = {
    POST_PROJECT_ASSOCIATION_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/project-associations',
        handlerMethod: 'addCustomFieldProjectAssociation',
        publicApi: true,
        description: 'Associate a custom field to a project',
        group: Group.CustomFields,
        zod: {
            params: customFieldParamsSchema,
            body: z.object({
                projectUuid: z.string().uuid().optional()
            })
        },
        responses: {
            200: {
                description: 'The created custom field association',
                schema: customFieldAssociationSchema
            }
        }
    },
    POST_FILE_ASSOCIATION_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/file-associations',
        handlerMethod: 'addCustomFieldFileAssociation',
        publicApi: true,
        description: 'Associate a custom field to a file',
        group: Group.CustomFields,
        zod: {
            params: customFieldParamsSchema,
            body: z.object({
                fileUuid: z.string().optional()
            })
        },
        responses: {
            200: {
                description: 'The created custom field association',
                schema: customFieldAssociationSchema
            }
        }
    },
    POST_FOLDER_ASSOCIATION_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/folder-associations',
        handlerMethod: 'addCustomFieldFolderAssociation',
        publicApi: true,
        description: 'Associate a custom field to a folder',
        group: Group.CustomFields,
        zod: {
            params: customFieldParamsSchema,
            body: z.object({
                folderUuid: z.string().optional()
            })
        },
        responses: {
            200: {
                description: 'The created custom field association',
                schema: customFieldAssociationSchema
            }
        }
    },
    DELETE_OA: {
        method: ApiMethod.DELETE,
        path: '/public/v1/workspaces/:workspaceName/custom-fields/:customFieldUuid/associations/:associationUuid',
        handlerMethod: 'removeCustomFieldAssociation',
        publicApi: true,
        description: 'Remove a custom field association',
        group: Group.CustomFields,
        zod: {
            params: customFieldAssociationParamsSchema
        },
        responses: {
            204: {
                description: 'The custom field association was successfully removed'
            }
        }
    }
} as const;
